import { normalize } from 'normalizr'
import { takeEvery, call, put, select } from 'redux-saga/effects'
import { getAsyncInfo } from 'selectors/application'
import { identity } from 'ramda'
import {
  API_CALL,
  API_ERROR,
  addToAsyncSet,
  removeFromAsyncSet,
} from 'actions/application'
import {
  fetchFailureHandler,
  timeoutHandler,
  defaultErrorHandler,
  HANDLED,
  SKIP,
} from 'sagas/errorHandlers'

/**
 * Watcher saga — intercepts every API_CALL action dispatched via `apiAction`.
 *
 * See `actions/application.js` → `apiAction` for the full list of supported
 * parameters and `sagas/errorHandlers/index.js` for documentation on the
 * error handler chain.
 */
export default function* watchApiCall() {
  yield takeEvery(API_CALL, callApi)
}

/**
 * Named export for unit testing only. The worker is not part of the public
 * module API — import it only from test files via:
 *   import { callApiWorker } from 'sagas/apiCall'
 */
export { callApi as callApiWorker }

/**
 * Worker saga — executes a single API call and runs the error handler chain
 * on failure.
 *
 * Error handling is split into two tiers:
 *
 * SYSTEM handlers (always first, cannot be overridden):
 *   1. fetchFailureHandler  — session expiry / network failure → SiteMinder redirect
 *   2. timeoutHandler       — NetworkTimeoutError → timeout variant of ErrorModal
 *
 * USER handlers (configurable via `errorHandlers` in apiAction):
 *   3. ...errorHandlers     — domain-specific handlers (optional, ordered)
 *   4. defaultErrorHandler  — generic ErrorModal catch-all (always last)
 *
 * The failure action type (`failureActionType`) is always dispatched after the
 * handler chain runs, so reducers can react to errors independently of what
 * the UI showed. System handlers that redirect/short-circuit are the exception:
 * they return early before the failure action dispatch.
 *
 * @param {object} action - The API_CALL action produced by `apiAction`.
 */
function* callApi({
  asyncFn,
  args,
  successActionType,
  id,
  failureActionType = null,
  requestActionType,
  schema,
  resolve = identity,
  reject = identity,
  conditionalSelector,
  errorHandlers = [],
}) {
  // Skip if a conditional selector indicates the data is already loaded.
  if (conditionalSelector) {
    const alreadyCalled = yield select(conditionalSelector)
    if (alreadyCalled) return resolve()
  }

  const transformData = (data) => (schema ? normalize(data, schema) : data)

  const callSignal = `${successActionType}${id || ''}`
  try {
    // De-dupe check BEFORE dispatching requestActionType: checking before the
    // request dispatch prevents reducers from entering a loading state for a
    // call that will never complete.
    //
    // When a call is skipped we resolve with undefined (not reject) so the
    // Promise settles silently — deduplication is a normal operating condition,
    // not a user error. Callers that check response?.data receive undefined and
    // treat it as a no-op. Plain apiAction callers use identity (no-op).
    //
    // NOTE — addToAsyncSet has not been called yet at this point, so no
    // async-set cleanup is needed on this early return.
    const { waitingSignals } = yield select(getAsyncInfo)
    if (!callSignal || waitingSignals.has(callSignal)) {
      // De-duplicated call: resolve with undefined so the Promise settles
      // silently. This is a normal operating condition — callers should treat
      // an undefined result as "call was already in flight, no-op". Plain
      // apiAction callers use identity (no-op). promiseApiAction callers that
      // need the response should guard: const data = response?.data ?? response.
      resolve(undefined)
      return null
    }

    if (requestActionType)
      yield put({
        type: requestActionType,
        ...args,
      })

    yield put(addToAsyncSet({ signal: callSignal }))

    // asyncFn contract: `asyncFn` MUST return a plain object with a `data` key
    // (or the full response shape for endpoints that also use `result`).
    //
    // Correct:   async (args) => apiRequest(...) // apiRequest returns { data, result }
    // Correct:   async (args) => ({ data: someValue })
    // Wrong:     async (args) => someValue        // data will be undefined
    //
    // If `response.data === undefined`, the line below falls back to the full
    // response as the data payload — this is a safety net for legacy service
    // functions that return a raw value, but all new service functions should
    // always return the `{ data }` shape so normalisation and result handling work.
    const response = yield call(asyncFn, args)
    const data = response.data !== undefined ? response.data : response
    const { result } = response
    const successAction = {
      type: successActionType,
      ...args,
      ...(result ? { ...transformData(data) } : { data: transformData(data) }),
    }

    // Remove signal before resolving: removeFromAsyncSet must come BEFORE
    // resolve() so that any awaiting caller that immediately re-dispatches the
    // same action sees the signal absent and isn't incorrectly rejected as a
    // duplicate.
    //
    // Redux state must also be updated before the Promise resolves so that any
    // .then() handler reading a selector sees the latest store state.
    yield put(successAction)
    yield put(removeFromAsyncSet({ signal: callSignal }))
    resolve(successAction)
  } catch (err) {
    const context = {
      args,
      successActionType,
      failureActionType,
      id,
      callSignal,
    }

    // ── Tier 1: System handlers ────────────────────────────────────────────
    // These run before any user-defined handlers and cannot be suppressed.
    // When a system handler returns HANDLED (auth redirect or timeout), cleanup
    // still runs: the failure action is dispatched (so reducers clear isLoading),
    // the async signal is removed, and the promise is rejected. The page then
    // redirects or shows the timeout overlay — but state is left consistent.
    //
    // ⚠ Modal trade-off: because system handlers run first, a timeout or
    // network failure for a request that uses createModalErrorHandler will show
    // the system overlay (timeout modal / SiteMinder redirect) rather than the
    // in-modal error state. The modal remains open behind the overlay. This is
    // an accepted architectural trade-off: system-level failures represent
    // unrecoverable states where the user must reload/re-authenticate, so
    // in-modal recovery UX is not meaningful. Document this at call sites if
    // the UX difference is important for a specific flow.
    //
    // Before redirecting we must still:
    //   1. Dispatch the failure action (if provided) so reducers clear isLoading.
    //   2. Remove the in-flight signal so the async set is consistent.
    //   3. Reject the promise so promiseApiAction callers don't hang forever.
    const systemHandlers = [fetchFailureHandler, timeoutHandler]
    const systemResult = yield* runHandlerChain(systemHandlers, err, context)
    if (systemResult === HANDLED) {
      if (failureActionType) {
        // Use the same full failure action shape as the user handler path so
        // reducers reading action.code / action.description / action.stack get
        // consistent values regardless of which handler tier caught the error.
        const { code, description, stack } = err
        yield put({
          type: failureActionType,
          errorMessage: err.toString(),
          error: err.toString(),
          code,
          description,
          stack,
          ...(!!args && { ...args, actionArguments: args }),
        })
      }
      yield put(removeFromAsyncSet({ signal: callSignal }))
      reject(err)
      return null
    }

    // ── Tier 2: User handlers ──────────────────────────────────────────────
    // Domain-specific handlers supplied via `errorHandlers`, followed by the
    // default catch-all. The first handler to return HANDLED stops the chain.
    //
    // ── Defensive wrapper ──────────────────────────────────────────────────
    // If a user handler throws (e.g. a badly-written custom handler or a
    // createModalErrorHandler callback that throws), execution must NOT skip
    // the cleanup below. We catch the handler exception and fall through to
    // the finally block so removeFromAsyncSet, the failure action dispatch,
    // and promise rejection always run.
    //
    // The handler exception is logged but otherwise swallowed here because
    // the original API error is still the one we want to dispatch and reject
    // with — the handler failure is a secondary implementation bug.
    const originalErr = err
    let handlerErr = err
    try {
      yield* runHandlerChain(
        [...errorHandlers, defaultErrorHandler],
        err,
        context
      )
    } catch (handlerThrown) {
      // eslint-disable-next-line no-console
      console.error(
        '[apiCall] Error handler threw unexpectedly — cleanup will still run:',
        handlerThrown
      )
      handlerErr = handlerThrown
    } finally {
      // ── Failure action dispatch ──────────────────────────────────────────
      // Always dispatch the failure action so reducers can update loading/error
      // state regardless of which UI handler ran. This keeps the handler chain
      // (UI concerns) decoupled from state management (reducer concerns).
      //
      // IMPORTANT: always use the ORIGINAL API error (originalErr) for the
      // failure action dispatch — not handlerErr. If a user handler threw an
      // internal exception, handlerErr is the handler's crash, not the API
      // error. Reducers reading action.code / action.description / action.error
      // expect the API error shape; a handler crash object would produce
      // incorrect reducer state and potentially confusing user-visible feedback.
      //
      // handlerErr is still passed to reject() so that promiseApiAction callers
      // can distinguish a handler crash (secondary bug) from a normal API error.
      //
      // `...args` spreads call arguments (e.g. serialNumber) to the top level so
      // legacy reducers can read them directly (e.g. action.serialNumber).
      // `error` is an alias for `errorMessage` for backward compat with reducers
      // that still read action.error.
      //
      // Always use API_ERROR as the fallback, never err.constructor.name —
      // production minification mangles class names, making constructor.name
      // unreliable. Callers must pass an explicit failureActionType; if they
      // don't, we dispatch under the generic API_ERROR type.
      const errorType = failureActionType || API_ERROR
      const errorMessage = originalErr.toString()
      const { code, description, stack } = originalErr

      yield put({
        type: errorType,
        errorMessage,
        error: errorMessage,
        code,
        description,
        stack,
        ...(!!args && { ...args, actionArguments: args }),
        ...(!!callSignal && { signal: callSignal, args }),
      })

      yield put(removeFromAsyncSet({ signal: callSignal }))
      reject(handlerErr)
    }
  }
  // Generator return value is unused by takeEvery — all exit paths are handled
  // within the try/catch above.
  return null
}

/**
 * Runs an array of error handler generators in sequence.
 *
 * Iterates with an index-based while loop (rather than for...of) to satisfy
 * the ESLint `no-restricted-syntax` rule that bans iterator usage.
 *
 * Stops and returns `HANDLED` as soon as a handler returns `HANDLED`.
 * Any non-HANDLED return value (SKIP or anything else) advances to the next handler.
 *
 * Non-function entries are skipped with a console.error rather than throwing —
 * a bad entry must not crash the saga and leave the async-set/loading state stuck.
 *
 * @param {Generator[]} handlers - Ordered array of handler generator functions.
 * @param {Error}       err      - The caught error.
 * @param {object}      context  - Context forwarded to each handler.
 * @returns {'handled'|'skip'}   - `HANDLED` if any handler consumed the error,
 *                                  `SKIP` if all handlers passed.
 */
function* runHandlerChain(handlers, err, context) {
  let i = 0
  while (i < handlers.length) {
    if (typeof handlers[i] !== 'function') {
      // eslint-disable-next-line no-console
      console.error(
        '[runHandlerChain] Skipping non-function handler at index',
        i,
        handlers[i]
      )
    } else {
      // eslint-disable-next-line no-await-in-loop
      const result = yield* handlers[i](err, context)
      if (result === HANDLED) return HANDLED
    }
    i += 1
  }
  return SKIP
}
